import { styled,
    experimental_sx as sx
 } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Title from '../../shared/Title';
import FFPaper from '../../shared/FFPaper';
import BlueSkeleton from '../../shared/BlueSkeleton';
import DropDownButton, { DropDownMenu } from '../../shared/DropDownButton';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { VisionAwareContext } from '../../../context/visionaware-context';
import { useTranslation } from 'react-i18next';

const BoxCircle = styled(Box)(({ theme }) => theme.unstable_sx({
    border: '1px solid lightgray',
    borderRadius: '50%',
    width: '34px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mr: 2,
    ml: 1,
}));

const ROILabel = styled(Typography)(({ theme }) => theme.unstable_sx({
    textAlign: 'left',
    flex: '1 0 50%',
    fontSize: {
        xs: 14,
        sm: 16,
        lg: 16,
        md: 16,
        xl: 16,
        xxl: 16
    },
    color: 'primary.main',
    ml: 0,
    pl: 0
}));

const ROIValue = styled(Typography)(({ theme }) => theme.unstable_sx({
    textAlign: 'left',
    fontSize: {
        xs: 16,
        sm: 20,
        lg: 22,
        xl: 24,
    },
    pl: {
        xs: 7,
        sm: 0,
        md: 7,
        lg: 7,
        xl: 0,
        xxl: 0
    },
    color: 'text.secondary',
    flex: '1 0 25%',
    fontWeight: '600'
}));


export default function TotalROICard() {
    const { t } = useTranslation();

    const menus: DropDownMenu[] = [{ label: 'Year-to-date', value: 'ytd' }, { label: 'Month-to-date', value: 'mtd' }]

    return (
        <VisionAwareContext.Consumer>{
            ({ status, data, isFetching, error }) =>
                <Grid item={true} xs={12} >
                    <FFPaper
                        elevation={3}
                        sx={{
                            position: 'relative',
                            height: '100%',
                            pl: 3,
                            pr: 3,
                            pb: 2,
                            pt: 2
                        }}
                    >
                        <Stack direction="row" sx={{
                            width: '100%',
                            pb: 2,
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Stack sx={{
                                justifyContent: 'center',
                                flex: '1 0 90%',
                                fontWeight: '500',
                                textTransform: 'uppercase',
                            }}>
                                <Title minWidth={250} info='Measures the total ROI for the year or month to date'>{t('va_total_roi_title')}</Title>
                            </Stack>
                            <Stack sx={{
                                justifyContent: 'center',
                                flex: '1 0 10%',
                                pl: {
                                    xs: 0,
                                    sm: 0,
                                    lg: 0,
                                    xl: 1,
                                    xxl: 1
                                },
                                pt: {
                                    xs: 1,
                                    sm: 1,
                                    lg: 1,
                                    xl: 0,
                                    xxl: 0
                                },
                            }}>
                            </Stack>
                        </Stack>

                        <Divider />

                        <Stack 
                            spacing={1}
                            sx={{
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography color="primary" sx={{
                                fontWeight: '500',
                                fontSize: {
                                    xs: 14,
                                    md: 16,
                                    textTransform: 'uppercase',
                                },
                                color: '#686868',
                                textAlign: 'left',
                                mt: 2
                            }}>{t('va_total_roi_subtitle')}</Typography>

                            <Stack direction="row" spacing={2}
                                sx={{
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    fontWeight: '500',
                                    fontSize: {
                                        xs: 14,
                                        md: 16,
                                        textTransform: 'uppercase',
                                    },
                                    color: '#686868'
                                }}
                            >
                                <BoxCircle>
                                    <RemoveRoundedIcon
                                        color="secondary" />
                                </BoxCircle>

                                <ROILabel>{t('va_total_roi_1')}</ROILabel>

                                <ROIValue>
                                        {isFetching && <BlueSkeleton width={80} />}
                                        {status === 'success' && !isFetching && '$' + (data?.totalLossDeterred ? Math.round(+data.totalLossDeterred).toLocaleString() : 0)}
                                </ROIValue>

                            </Stack>

                            <Stack direction="row" spacing={2}
                                sx={{
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    fontWeight: '500',
                                    fontSize: {
                                        xs: 14,
                                        md: 16,
                                        textTransform: 'uppercase',
                                    },
                                    color: '#686868'
                                }}
                            >
                                <BoxCircle>
                                    <AccessTimeRoundedIcon
                                        color="secondary" />
                                </BoxCircle>

                                <ROILabel>{t('va_total_roi_2')}</ROILabel>
                                
                                <ROIValue>
                                        {isFetching && <BlueSkeleton width={80} />}
                                        {status === 'success' && !isFetching && data?.totalROITimeSaved + ' Hrs'}
                                </ROIValue>

                            </Stack>

                            <Stack direction="row" spacing={2}
                                sx={{
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    fontWeight: '500',
                                    fontSize: {
                                        xs: 14,
                                        md: 16,
                                        textTransform: 'uppercase',
                                    },
                                    color: '#686868'
                                }}
                            >
                                <BoxCircle>
                                    <VerifiedUserOutlinedIcon
                                        fontSize="small"
                                        color="secondary" />
                                </BoxCircle>

                                <ROILabel>{t('va_total_roi_3')}</ROILabel>

                                <ROIValue>
                                        {isFetching && <BlueSkeleton width={80} />}
                                        {status === 'success' && !isFetching && data?.totalROISeriousEventsDeterred}
                                </ROIValue>

                            </Stack>

                        </Stack>

                    </FFPaper>

                </Grid>
        }
        </VisionAwareContext.Consumer>
    )

}